import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  archiveProductMedia,
  completeProductMediaUpload,
  getCommerceErrorMessage,
  getCommerceErrorStatus,
  initProductMediaUpload,
  replaceProductMediaOrder,
  updateProductMedia,
  uploadProductMediaFile,
  type Product,
  type ProductMediaPlacement,
  type ProductMediaUploadInitResponse,
} from '@/lib/commerce';
import type {
  ProductMediaManagerViewModel,
  ProductMediaPlacementFormValue,
  ProductMediaPreviewState,
  ProductMediaUploadStatus,
} from './headless-contract';

const acceptedMimeTypes = ['image/jpeg', 'image/png', 'image/webp'];
const maxFileSizeBytes = 10 * 1024 * 1024;

const roleOptions = [
  { value: 'primary', label: 'Primary', description: 'The default product image.' },
  { value: 'thumbnail', label: 'Thumbnail', description: 'A compact browse image.' },
  { value: 'gallery', label: 'Gallery', description: 'An additional product image.' },
  { value: 'hero', label: 'Hero', description: 'A wide promotional product image.' },
];

const emptyPlacementForm = (sortOrder = 0): ProductMediaPlacementFormValue => ({
  role: 'gallery',
  appliesTo: 'product',
  variantId: null,
  altText: '',
  caption: '',
  sortOrder,
});

const placementForm = (placement: ProductMediaPlacement): ProductMediaPlacementFormValue => ({
  role: placement.role || 'gallery',
  appliesTo: placement.variant_id ? 'variant' : 'product',
  variantId: placement.variant_id ?? null,
  altText: placement.alt_text ?? '',
  caption: placement.caption ?? '',
  sortOrder: placement.sort_order,
});

const formatBytes = (bytes?: number | null) => {
  if (!bytes || bytes < 1) return null;
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const fileNameForPlacement = (placement: ProductMediaPlacement) => {
  const storageKey = placement.asset.storage_key?.trim();
  if (storageKey) {
    try {
      const pathname = storageKey.startsWith('http://') || storageKey.startsWith('https://')
        ? new URL(storageKey).pathname
        : storageKey;
      return pathname.split('/').filter(Boolean).pop() || placement.asset.asset_id;
    } catch {
      return storageKey.split('/').filter(Boolean).pop() || placement.asset.asset_id;
    }
  }
  return placement.asset.asset_id;
};

const mediaUrlForPlacement = (placement: ProductMediaPlacement) => {
  const resolved = placement.asset.media_url?.trim();
  if (resolved) return resolved;
  return '';
};

const validateFile = (file: File | null) => {
  if (!file) return 'Choose an image before uploading.';
  if (!acceptedMimeTypes.includes(file.type.toLowerCase())) {
    return 'Choose a JPEG, PNG, or WebP image.';
  }
  if (file.size > maxFileSizeBytes) {
    return 'Choose an image no larger than 10 MiB.';
  }
  if (file.size < 1) {
    return 'Choose an image that is not empty.';
  }
  return null;
};

export interface UseCatalogProductMediaControllerOptions {
  product: Product | null;
  loading: boolean;
  loadError?: string | null;
  refreshProduct: () => Promise<Product | null>;
  onAccessDenied: (status: number) => void;
  onError: (message: string) => void;
  onSuccess: (title: string, description?: string) => void;
  confirmDelete: (title: string, description: string, onConfirm: () => void) => void;
}

export function useCatalogProductMediaController({
  product,
  loading,
  loadError,
  refreshProduct,
  onAccessDenied,
  onError,
  onSuccess,
  confirmDelete,
}: UseCatalogProductMediaControllerOptions): ProductMediaManagerViewModel {
  const [selectedPlacementId, setSelectedPlacementId] = useState<string | null>(null);
  const [previewStates, setPreviewStates] = useState<Record<string, ProductMediaPreviewState>>({});
  const refreshedPreviewIds = useRef(new Set<string>());
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingPlacementId, setEditingPlacementId] = useState<string | null>(null);
  const [form, setForm] = useState<ProductMediaPlacementFormValue>(() => emptyPlacementForm());
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [editorError, setEditorError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [localPreviewUrl, setLocalPreviewUrl] = useState('');
  const [uploadStatus, setUploadStatus] = useState<ProductMediaUploadStatus>('idle');
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [bytesTransferred, setBytesTransferred] = useState(0);
  const [totalBytes, setTotalBytes] = useState(0);
  const [pendingUpload, setPendingUpload] = useState<ProductMediaUploadInitResponse | null>(null);

  const placements = product?.media ?? [];

  useEffect(() => {
    if (placements.length === 0) {
      setSelectedPlacementId(null);
      return;
    }
    if (!selectedPlacementId || !placements.some(item => item.placement_id === selectedPlacementId)) {
      setSelectedPlacementId(placements[0].placement_id);
    }
  }, [placements, selectedPlacementId]);

  useEffect(() => () => {
    if (localPreviewUrl) URL.revokeObjectURL(localPreviewUrl);
  }, [localPreviewUrl]);

  const chooseFile = useCallback((nextFile: File | null) => {
    setLocalPreviewUrl(current => {
      if (current) URL.revokeObjectURL(current);
      return nextFile ? URL.createObjectURL(nextFile) : '';
    });
    setFile(nextFile);
    setPendingUpload(null);
    setBytesTransferred(0);
    setTotalBytes(nextFile?.size ?? 0);
    setUploadError(null);
    const fileError = validateFile(nextFile);
    setValidationErrors(current => {
      const next = { ...current };
      if (fileError) next.file = fileError;
      else delete next.file;
      return next;
    });
    setUploadStatus(nextFile ? (fileError ? 'error' : 'selected') : 'idle');
    if (nextFile) {
      setEditingPlacementId(null);
      setForm(current => ({ ...emptyPlacementForm(placements.length * 10), role: current.role || 'gallery' }));
      setEditorOpen(true);
    }
  }, [placements.length]);

  const reportFailure = useCallback((error: unknown, fallback: string) => {
    const status = getCommerceErrorStatus(error);
    if (status === 401 || status === 403) onAccessDenied(status);
    const message = getCommerceErrorMessage(error, fallback);
    onError(message);
    return message;
  }, [onAccessDenied, onError]);

  const runUpload = useCallback(async () => {
    if (!product) return;
    const fileError = validateFile(file);
    const nextValidation: Record<string, string> = {};
    if (fileError) nextValidation.file = fileError;
    if (!form.role.trim()) nextValidation.role = 'Choose an image role.';
    if (form.appliesTo === 'variant' && !form.variantId) nextValidation.variantId = 'Choose a variant.';
    if (!Number.isFinite(form.sortOrder) || form.sortOrder < 0) nextValidation.sortOrder = 'Enter a non-negative order.';
    setValidationErrors(nextValidation);
    if (Object.keys(nextValidation).length > 0 || !file) {
      setUploadStatus('error');
      setUploadError(fileError || 'Review the image details before uploading.');
      return;
    }

    setSaving(true);
    setEditorError(null);
    setUploadError(null);
    try {
      let session = pendingUpload;
      const expiresAt = session ? Date.parse(session.expires_at) : 0;
      if (!session || !Number.isFinite(expiresAt) || expiresAt <= Date.now() + 5_000) {
        setUploadStatus('initializing');
        session = await initProductMediaUpload(product.product_id, {
          filename: file.name,
          mime_type: file.type,
          file_size_bytes: file.size,
        });
        setPendingUpload(session);
      }

      setUploadStatus('uploading');
      await uploadProductMediaFile(session.presigned_url, file, {
        onProgress: progress => {
          setBytesTransferred(progress.loadedBytes);
          setTotalBytes(progress.totalBytes);
        },
      });

      setUploadStatus('completing');
      const placement = await completeProductMediaUpload(product.product_id, {
        asset_id: session.asset_id,
        storage_provider: session.storage_provider,
        storage_key: session.storage_key,
        mime_type: file.type,
        file_size_bytes: file.size,
        role: form.role.trim(),
        variant_id: form.appliesTo === 'variant' ? form.variantId || undefined : undefined,
        alt_text: form.altText?.trim() || undefined,
        caption: form.caption?.trim() || undefined,
        sort_order: form.sortOrder,
      });
      await refreshProduct();
      setSelectedPlacementId(placement.placement_id);
      setUploadStatus('success');
      setPendingUpload(null);
      setEditorOpen(false);
      setFile(null);
      setLocalPreviewUrl(current => {
        if (current) URL.revokeObjectURL(current);
        return '';
      });
      onSuccess('Product image uploaded', product.name);
    } catch (error) {
      const message = reportFailure(error, 'Failed to upload product image.');
      setUploadStatus('error');
      setUploadError(message);
    } finally {
      setSaving(false);
    }
  }, [file, form, onSuccess, pendingUpload, product, refreshProduct, reportFailure]);

  const savePlacement = useCallback(async () => {
    if (!editingPlacementId) {
      await runUpload();
      return;
    }
    const nextValidation: Record<string, string> = {};
    if (!form.role.trim()) nextValidation.role = 'Choose an image role.';
    if (form.appliesTo === 'variant' && !form.variantId) nextValidation.variantId = 'Choose a variant.';
    if (!Number.isFinite(form.sortOrder) || form.sortOrder < 0) nextValidation.sortOrder = 'Enter a non-negative order.';
    setValidationErrors(nextValidation);
    if (Object.keys(nextValidation).length > 0) return;

    setSaving(true);
    setEditorError(null);
    try {
      await updateProductMedia(editingPlacementId, {
        role: form.role.trim(),
        variant_id: form.appliesTo === 'variant' ? form.variantId || undefined : null,
        alt_text: form.altText?.trim() || undefined,
        caption: form.caption?.trim() || undefined,
        sort_order: form.sortOrder,
      });
      await refreshProduct();
      setEditorOpen(false);
      onSuccess('Product image updated', product?.name);
    } catch (error) {
      setEditorError(reportFailure(error, 'Failed to update product image.'));
    } finally {
      setSaving(false);
    }
  }, [editingPlacementId, form, onSuccess, product?.name, refreshProduct, reportFailure, runUpload]);

  const items = useMemo(() => placements.map(placement => {
    const mediaUrl = mediaUrlForPlacement(placement);
    return {
      placementId: placement.placement_id,
      assetId: placement.asset.asset_id,
      fileName: fileNameForPlacement(placement),
      role: placement.role,
      sortOrder: placement.sort_order,
      mediaUrl,
      mediaUrlExpiresAt: placement.asset.media_url_expires_at,
      previewState: previewStates[placement.placement_id] ?? (mediaUrl ? 'ready' : 'error'),
      previewErrorMessage: mediaUrl ? null : 'This image does not have a browser-usable URL.',
      variantLabel: placement.variant_id
        ? product?.variants.find(variant => variant.variant_id === placement.variant_id)?.display_name || placement.variant_id
        : null,
      altText: placement.alt_text,
      caption: placement.caption,
      mimeTypeLabel: placement.asset.mime_type,
      dimensionsLabel: placement.asset.width && placement.asset.height
        ? `${placement.asset.width} × ${placement.asset.height}`
        : null,
      fileSizeLabel: formatBytes(placement.asset.file_size_bytes),
      uploadOriginLabel: placement.asset.storage_provider === 's3' ? 'Commerce storage' : 'External URL',
    };
  }), [placements, previewStates, product?.variants]);

  const openCreate = useCallback(() => {
    setEditingPlacementId(null);
    setForm(emptyPlacementForm(placements.length * 10));
    setValidationErrors({});
    setEditorError(null);
    setEditorOpen(true);
  }, [placements.length]);

  const openEdit = useCallback((placementId: string) => {
    const placement = placements.find(item => item.placement_id === placementId);
    if (!placement) return;
    setEditingPlacementId(placementId);
    setForm(placementForm(placement));
    setValidationErrors({});
    setEditorError(null);
    setEditorOpen(true);
  }, [placements]);

  const deletePlacement = useCallback((placementId: string) => {
    const placement = placements.find(item => item.placement_id === placementId);
    if (!placement) return;
    confirmDelete(
      'Delete product image?',
      `“${fileNameForPlacement(placement)}” will be removed from this product.`,
      () => {
        void (async () => {
          try {
            await archiveProductMedia(placementId);
            await refreshProduct();
            onSuccess('Product image removed', product?.name);
          } catch (error) {
            reportFailure(error, 'Failed to remove product image.');
          }
        })();
      },
    );
  }, [confirmDelete, onSuccess, placements, product?.name, refreshProduct, reportFailure]);

  const reorder = useCallback(async (placementIds: string[]) => {
    if (!product) return;
    try {
      await replaceProductMediaOrder(product.product_id, placementIds);
      await refreshProduct();
    } catch (error) {
      reportFailure(error, 'Failed to reorder product images.');
    }
  }, [product, refreshProduct, reportFailure]);

  const refreshPreview = useCallback((placementId: string, automatic: boolean) => {
    if (automatic && refreshedPreviewIds.current.has(placementId)) {
      setPreviewStates(current => ({ ...current, [placementId]: 'error' }));
      return;
    }
    if (automatic) refreshedPreviewIds.current.add(placementId);
    setPreviewStates(current => ({ ...current, [placementId]: 'refreshing' }));
    void refreshProduct()
      .then(nextProduct => {
        const refreshed = nextProduct?.media.find(item => item.placement_id === placementId);
        setPreviewStates(current => ({
          ...current,
          [placementId]: refreshed && mediaUrlForPlacement(refreshed) ? 'ready' : 'error',
        }));
      })
      .catch(() => {
        setPreviewStates(current => ({ ...current, [placementId]: 'error' }));
      });
  }, [refreshProduct]);

  const previewError = useCallback((placementId: string) => {
    refreshPreview(placementId, true);
  }, [refreshPreview]);

  const retryPreview = useCallback((placementId: string) => {
    refreshPreview(placementId, false);
  }, [refreshPreview]);

  const progressPercent = totalBytes > 0 ? Math.min(100, Math.round((bytesTransferred / totalBytes) * 100)) : 0;
  const state = loading ? 'loading' : loadError ? 'error' : saving ? 'saving' : placements.length ? 'ready' : 'empty';

  return {
    state,
    title: 'Images',
    description: 'Curate the product gallery, primary image, and accessible image details.',
    emptyTitle: 'No product images',
    emptyBody: 'Upload a JPEG, PNG, or WebP image to start this product gallery.',
    errorMessage: loadError,
    selectedPlacementId,
    items,
    roleOptions,
    callouts: [],
    upload: {
      status: uploadStatus,
      acceptedMimeTypes,
      acceptedTypesLabel: 'JPEG, PNG, or WebP',
      maxFileSizeBytes,
      maxFileSizeLabel: '10 MiB',
      selectedFile: file && localPreviewUrl ? {
        fileName: file.name,
        fileSizeLabel: formatBytes(file.size) || '0 KB',
        mimeTypeLabel: file.type,
        localPreviewUrl,
      } : null,
      bytesTransferred,
      totalBytes,
      progressPercent,
      statusLabel: uploadStatus === 'initializing'
        ? 'Preparing upload'
        : uploadStatus === 'uploading'
          ? 'Uploading image'
          : uploadStatus === 'completing'
            ? 'Finalizing image'
            : uploadStatus === 'success'
              ? 'Upload complete'
              : null,
      errorMessage: uploadError,
      submitDisabled: saving || !file || Boolean(validateFile(file)),
      canRetry: uploadStatus === 'error' && Boolean(file) && !validateFile(file),
    },
    placementEditor: {
      mode: editingPlacementId ? 'edit' : 'create',
      isOpen: editorOpen,
      title: editingPlacementId ? 'Edit product image' : 'Upload product image',
      saveActionLabel: editingPlacementId ? 'Save changes' : 'Upload image',
      placementId: editingPlacementId,
      value: form,
      roleOptions,
      variantOptions: (product?.variants ?? []).map(variant => ({
        value: variant.variant_id,
        label: variant.display_name || variant.name,
      })),
      fieldHelp: [],
      validationErrors,
      errorMessage: editorError,
      saving,
      onChange: patch => setForm(current => ({ ...current, ...patch })),
      onSave: savePlacement,
      onClose: () => setEditorOpen(false),
    },
    onRetryLoad: () => { void refreshProduct(); },
    onSelectAsset: setSelectedPlacementId,
    onChooseFile: chooseFile,
    onDropFile: chooseFile,
    onClearFile: () => chooseFile(null),
    onUpload: runUpload,
    onRetryUpload: runUpload,
    onPreviewError: previewError,
    onRetryPreview: retryPreview,
    onAddAsset: openCreate,
    onEditAsset: openEdit,
    onDeleteAsset: deletePlacement,
    onReorder: reorder,
  };
}
